import { useEffect, useState } from 'react';
import type { HoldList, ShowList } from 'api-client/models';
import { useAppContext, useHoldsApi, useShowsApi } from '@/context/AppContext';
import { apiDateToDateOnly, localDateToDateOnly } from '@/shows/utils/dateOnly';

interface CalendarData {
  teamId: number;
  date: Date;
  shows: ShowList[];
  holds: HoldList[];
  error: string | null;
}

export function useCalendarData(currentDate: Date) {
  const { team } = useAppContext();
  const teamId = team?.id;
  const showsApi = useShowsApi();
  const holdsApi = useHoldsApi();
  const [data, setData] = useState<CalendarData | null>(null);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (teamId == null) return;
    let cancelled = false;
    setLoading(true);
    setData(null);

    const load = async () => {
      // Include the trailing/leading days displayed around the selected month.
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startingDay = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const remainingDays = 7 - ((startingDay + daysInMonth) % 7);
      const start = localDateToDateOnly(new Date(year, month, 1 - startingDay));
      const end = localDateToDateOnly(
        new Date(year, month + 1, remainingDays < 7 ? remainingDays + 7 : 7),
      );
      const inRange = (date: Date | null | undefined): boolean => {
        if (!date) return false;
        const value = apiDateToDateOnly(date);
        return value >= start && value <= end;
      };
      // Follow pagination so a busy team does not silently lose calendar entries.
      const allPages = async <T>(
        fetchPage: (
          page: number,
        ) => Promise<{ results: T[]; next?: string | null }>,
      ) => {
        const results: T[] = [];
        for (let page = 1; !cancelled; page += 1) {
          const response = await fetchPage(page);
          results.push(...response.results);
          if (!response.next) break;
        }
        return results;
      };
      try {
        const [shows, holds] = await Promise.all([
          allPages((page) => showsApi.showsList({ team: teamId, page })),
          allPages((page) => holdsApi.holdsList({ team: teamId, page })),
        ]);
        if (!cancelled)
          setData({
            teamId,
            date: currentDate,
            shows: shows.filter(
              (show) => show.status === 'confirmed' && inRange(show.showDate),
            ),
            holds: holds.filter((hold) => inRange(hold.holdDate)),
            error: null,
          });
      } catch {
        if (!cancelled)
          setData({
            teamId,
            date: currentDate,
            shows: [],
            holds: [],
            error: 'Failed to load calendar. Please try again.',
          });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [teamId, currentDate, showsApi, holdsApi, revision]);

  // Never render the previous team's rows, even before the new effect runs.
  const current =
    data?.teamId === teamId && data?.date === currentDate ? data : null;
  return {
    shows: current?.shows ?? [],
    holds: current?.holds ?? [],
    loading: teamId != null && (loading || current == null),
    error:
      teamId == null ? 'Select a team to view its calendar.' : current?.error,
    refresh: () => setRevision((value) => value + 1),
  };
}
